/**
 * Custom React Hooks for Dashboard
 */

import { useState, useEffect, useCallback } from 'react';
import { GlobalMetrics, Alert, DashboardStats } from '../types';
import apiService from '../services/apiService';
import { API_BASE_URL } from '../config';

/**
 * Hook for fetching global metrics
 */
export function useGlobalMetrics(refetchInterval: number = 5000) {
  const [metrics, setMetrics] = useState<GlobalMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.metrics.getGlobalMetrics();
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch metrics'
      );
      console.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();

    // Set up interval for refetching
    if (refetchInterval > 0) {
      const interval = setInterval(fetchMetrics, refetchInterval);
      return () => clearInterval(interval);
    }
  }, [fetchMetrics, refetchInterval]);

  return { metrics, loading, error, refetch: fetchMetrics };
}

/**
 * Hook for fetching dashboard stats
 */
export function useDashboardStats(refetchInterval: number = 10000) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.metrics.getDashboardStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    if (refetchInterval > 0) {
      const interval = setInterval(fetchStats, refetchInterval);
      return () => clearInterval(interval);
    }
  }, [fetchStats, refetchInterval]);

  return { stats, loading, error, refetch: fetchStats };
}

/**
 * Hook for fetching alerts
 */
export function useAlerts(page: number = 1, pageSize: number = 10) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.alerts.getAlerts(page, pageSize);
      setAlerts(response.items);
      setTotal(response.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch alerts');
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return { alerts, total, loading, error, refetch: fetchAlerts };
}

/**
 * Hook for critical alerts
 */
export function useCriticalAlerts(limit: number = 5) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.alerts.getCriticalAlerts(limit);
      setAlerts(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch alerts');
      console.error('Error fetching critical alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchAlerts();

    const interval = setInterval(fetchAlerts, 3000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  return { alerts, loading, error, refetch: fetchAlerts };
}

/**
 * Hook for trending topics
 */
export function useTrendingTopics(limit: number = 5) {
  const [topics, setTopics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.topics.getTrendingTopics(limit);
      setTopics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch topics');
      console.error('Error fetching trending topics:', err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchTopics();

    const interval = setInterval(fetchTopics, 5000);
    return () => clearInterval(interval);
  }, [fetchTopics]);

  return { topics, loading, error, refetch: fetchTopics };
}

/**
 * Hook for chart data
 */
export function useChartData(
  fetchFn: () => Promise<any[]>,
  refetchInterval: number = 10000
) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const chartData = await fetchFn();
        setData(chartData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
        console.error('Error fetching chart data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetch();

    if (refetchInterval > 0) {
      const interval = setInterval(fetch, refetchInterval);
      return () => clearInterval(interval);
    }
  }, [fetchFn, refetchInterval]);

  return { data, loading, error };
}

/**
 * Hook for Network Graph Data (Phase 10)
 */
export function useNetworkData(limit: number = 100) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.analytics.getNetworkGraph(limit);
      setData(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error };
}

/**
 * Hook for Cluster Data (Phase 10)
 */
export function useClusterData() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.analytics.getCrisisClusters();
      setData(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error };
}

/**
 * Hook for Real-time WebSocket Updates (Phase 11)
 * Connects to the backend WebSocket for live data streaming
 */
export function useWebSocket() {
  const [messages, setMessages] = useState<any[]>([]);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Derive WS URL (http://... -> ws://..., https://... -> wss://...)
    // Handle the case where API_BASE_URL might be a relative path or missing
    let finalWsUrl = '';
    if (API_BASE_URL.startsWith('http')) {
      finalWsUrl = API_BASE_URL.replace(/^http/, 'ws') + '/ws/dashboard';
    } else {
      // Fallback for internal networking or development
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      finalWsUrl = `${protocol}//${host}/ws/dashboard`;
    }
    
    let ws: WebSocket;
    let reconnectTimer: any;

    const connect = () => {
      try {
        ws = new WebSocket(finalWsUrl);

        ws.onopen = () => {
          setIsConnected(true);
          console.log('📡 Dashboard WebSocket connected');
        };

        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          setMessages(prev => [data, ...prev].slice(0, 50)); 
        };

        ws.onclose = () => {
          setIsConnected(false);
          console.log('🛑 WebSocket disconnected. Retrying in 5s...');
          reconnectTimer = setTimeout(connect, 5000);
        };

        ws.onerror = (err) => {
          console.error('❌ WebSocket error:', err);
          ws.close();
        };
      } catch (err) {
        console.error('Failed to create WebSocket:', err);
        reconnectTimer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      if (ws) ws.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  return { messages, lastMessage, isConnected };
}
